import { ICognitoUserAttributeData } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import { RowNode } from '@ag-grid-community/core';
import { AttributeField } from '../store/users/types';
import { UserState, CognitoUser, Operations } from '../store/user/types';
import { Client, Company, CustomFieldOption } from '../store/clients/types';
import { User } from '../constants/dataTypes';
import { PortalConfig } from '../context';
import { SeverityLevel } from '../components/UI';
import { TrackEvent } from './AnalyticsUtils';
import DateUtils from './DateUtils';
import { EntityStatus } from '../constants';
import { MultiSelectOption } from '../components/Select/types';

type FieldsEntityType = Client | Company | User;

export const hasUserNameFields = (item: FieldsEntityType) =>
  Boolean(item.fields?.given_name || item.fields?.family_name);

export const getInitials = (name = '') =>
  name
    .replace(/\s+/, ' ')
    .split(' ')
    .slice(0, 2)
    .map(v => v && v[0].toUpperCase())
    .join('');

const getFullAddress = (address: string) => {
  const jsonAddress = JSON.parse(address);
  return `${jsonAddress.streetAddress}, ${jsonAddress.city}, ${jsonAddress.state} ${jsonAddress.zipCode}`;
};

// less specific check to see if the user is/is not archived or deleted
export const isActive = (user: User | Client) =>
  user.entityStatus !== EntityStatus.Archived &&
  user.entityStatus !== EntityStatus.Deleted;

/**
 * Check if the user is confirmed
 * @param user user/client entity being checked for status
 * @returns boolean value indicating whether the user should be considered "confirmed"
 */
export const isConfirmed = (user: User | Client) =>
  user?.fields.userStatus === 'CONFIRMED' ||
  (user?.fields.userStatus === 'FORCE_CHANGE_PASSWORD' &&
    !DateUtils.isEmptyDateStr(user?.fields.lastLogin));

// used to determine if a client is setup so that they would be able to access the portal
// if the user is confirmed they have a password they can use to login to the portal
export const isUserEnabled = (user: User | Client) =>
  (user.fields.userStatus === 'CONFIRMED' ||
    user.fields.userStatus === 'FORCE_CHANGE_PASSWORD') &&
  isActive(user);

export const getFieldValue = (user: User, fieldName: string) => {
  if (user && user.Attributes && user.Attributes.length) {
    const currentField = user.Attributes.find(
      (field: ICognitoUserAttributeData) => field.Name === fieldName,
    );

    if (fieldName === 'address') {
      return currentField ? getFullAddress(currentField.Value) : '';
    }
    if (fieldName === 'picture') {
      return currentField ? currentField.Value : '';
    }
    return currentField ? currentField.Value : '';
  }
  return '';
};

export const updateUserAttributes = (
  user: User,
  updatedFields: AttributeField[],
) => {
  const userData = { ...user };

  if (userData.Attributes && userData.Attributes.length) {
    if (updatedFields && updatedFields.length) {
      updatedFields.forEach(updatedField => {
        const indexOfField = userData.Attributes.findIndex(
          (field: ICognitoUserAttributeData) =>
            field.Name === updatedField.Name,
        );
        if (indexOfField > -1) {
          userData.Attributes[indexOfField] = updatedField;
        } else {
          userData.Attributes.push(updatedField);
        }
      });
    }
  } else {
    userData.Attributes = updatedFields;
  }
  return userData;
};

export const userTableNameComparator = (
  _a: any,
  _b: any,
  nodeA: RowNode,
  nodeB: RowNode,
) => (nodeA.data.userName || '').localeCompare(nodeB.data.userName || '');

export const hasPermission = (
  permissionName: string,
  permissions?: Record<string, Operations>,
) => {
  if (permissions) {
    if (permissions[permissionName]) {
      return true;
    }
  }
  return false;
};

export const hasOperation = (
  permissionName: string,
  operationName: string,
  permissions?: Record<string, Operations>,
) => {
  if (permissions) {
    const permission = permissions[permissionName];
    if (
      permission?.operations &&
      permission?.operations[operationName] !== null
    ) {
      return true;
    }
  }
  return false;
};

export const getCompanyId = (clientId: string, clients: User[]) =>
  clients.find(client => client.id === clientId)?.fields.companyId;

export const generatePassword = (passwordLengh: number) => {
  const uppercaseList = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercaseList = 'abcdefghijklmnopqrstuvwxyz';
  const numberList = '0123456789';

  const charset = uppercaseList + lowercaseList + numberList;
  let password = '';
  for (let i = 0, n = charset.length; i < passwordLengh; i += 1) {
    password += charset.charAt(Math.floor(Math.random() * n));
  }
  const indexOfUppercase = Math.floor(Math.random() * passwordLengh);
  let indexOfNumber = indexOfUppercase;
  while (indexOfNumber === indexOfUppercase) {
    indexOfNumber = Math.floor(Math.random() * passwordLengh);
  }
  password =
    password.substring(0, indexOfUppercase) +
    uppercaseList[Math.floor(Math.random() * uppercaseList.length)] +
    password.substring(indexOfUppercase + 1);
  password =
    password.substring(0, indexOfNumber) +
    numberList[Math.floor(Math.random() * numberList.length)] +
    password.substring(indexOfNumber + 1);
  return password;
};

export const getEmailDomain = (email: string) => {
  if (email) {
    return email.substring(email.lastIndexOf('@') + 1);
  }
  return '';
};

export const getEmailDomainList = (companies: Company[]) => {
  // TODO use companySearch property for getting company email domain
  // extracted email doman from user email for now
  const emailList = companies.map(company => ({
    companyId: company.id,
    emailDomains: company.fields ? company.fields.associatedDomains : [],
  }));
  return emailList;
};

export const formatStatus = (user: User) => {
  if (!isActive(user)) {
    return 'Deleted';
  }

  const { userStatus } = user.fields;
  const { invitedBy } = user.fields;

  if (userStatus === 'CONFIRMED') {
    return 'Active';
  }

  if (!invitedBy) {
    return 'Not Invited';
  }

  if (userStatus === 'FORCE_CHANGE_PASSWORD') {
    return 'Invited';
  }

  if (userStatus === 'UNCONFIRMED' || userStatus === 'RESET_REQUIRED') {
    return 'Pending Verification';
  }
  return 'N/A';
};

export const getUserAttributesValue = (
  instance: CognitoUser | null,
  fieldName: string,
) => {
  if (instance && instance.attributes) {
    let attributeValue = '';
    Object.keys(instance.attributes).forEach(key => {
      if (key === fieldName) {
        attributeValue = instance.attributes[key];
      }
    });

    return attributeValue;
  }
  return '';
};

export interface UserProfile {
  firstName: string;
  lastName: string;
  avatarUrl: string;
  email: string;
  fallbackColor: string;
  id: string;
}

const getProfileFieldValue = (profile: User, field: string) => {
  if (profile && profile.fields) {
    const currentField = profile.fields[field];

    return currentField || '';
  }
  return '';
};

export const getProfileFromUser = (user: User): UserProfile => ({
  firstName: getProfileFieldValue(user, 'given_name'),
  lastName: getProfileFieldValue(user, 'family_name'),
  avatarUrl: getProfileFieldValue(user, 'picture'),
  email: getProfileFieldValue(user, 'email'),
  id: user.id,
  fallbackColor: getProfileFieldValue(user, 'fallbackColor'),
});

export const getFullName = (user: User) =>
  `${getFieldValue(user, 'given_name')} ${getFieldValue(user, 'family_name')}`;

export const GetProfileFromChannelMember = (user: any): UserProfile => ({
  firstName: user.name ? user.name.trim().split(' ')[0] : undefined,
  lastName: user.name ? user.name.trim().split(' ')[1] : undefined,
  avatarUrl: user.image,
  email: '',
  id: user.id,
  fallbackColor: user.fallbackColor,
});

export const mapUserInstanceIntoUserEntityFields = (
  userInstance: UserState['instance'],
) => {
  if (userInstance !== null && userInstance.attributes) {
    const userAttributes = userInstance.attributes;
    const owner = {
      ...userAttributes,
      fallbackColor: userAttributes['custom:fallbackColor']
        ? userAttributes['custom:fallbackColor']
        : '',
    };

    return owner;
  }

  return null;
};

export const getUserIdFromFullId = (userFullId: string): string => {
  const splitedId = userFullId.split('_');
  const lastVal = splitedId.pop();

  return lastVal || '';
};

export const getFullUserIdFromUserId = (
  portalConfig: PortalConfig,
  userId: string,
): string => `${portalConfig.portalHeader}_${userId}`;

export const handleSignOut = async (
  portalConfig: PortalConfig,
  isTestUser: boolean,
) => {
  try {
    await Auth.signOut();
    TrackEvent(`Signed Out`, { isTestUser });
    // await dispatch(clearStoreData());
    window.location.replace(
      `/auth/signout?next=${
        portalConfig.MarketingSite.Enabled
          ? '/'
          : portalConfig.SignoutRedirectURL
      }`,
    );
  } catch (err) {
    // TODO: handle the error
  }
};

/**
 * @param status client/user status
 * @returns severity level regrding status
 */
export const mapStatusToSeverity = (status: string): SeverityLevel => {
  let severityLevel: SeverityLevel = SeverityLevel.low;
  switch (status.toLowerCase()) {
    case 'active':
      severityLevel = SeverityLevel.low;
      break;
    case 'invited':
      severityLevel = SeverityLevel.medium;
      break;
    case 'not invited':
      severityLevel = SeverityLevel.high;
      break;
    default:
      break;
  }

  return severityLevel;
};

export const nameComparator = (a: User, b: User) => {
  if (a.fields.given_name < b.fields.given_name) {
    return -1;
  }
  if (a.fields.given_name > b.fields.given_name) {
    return 1;
  }
  return 0;
};

export const userProfileComparator = (a: UserProfile, b: UserProfile) =>
  `${a.firstName}${a.lastName}`.localeCompare(`${b.firstName}${b.lastName}`);

const getMultiSelectSortString = (
  selectedOptIDs: string[],
  allOptions: MultiSelectOption[],
) => {
  const optionLabelReducer = (validOptions: string[], optionID: string) => {
    const validOption = allOptions.find(opt => opt.id === optionID);
    if (validOption) {
      return validOptions.concat(validOption.label);
    }
    return validOptions;
  };
  return selectedOptIDs
    .reduce(optionLabelReducer, [])
    .sort((labelA, labelB) => labelA.localeCompare(labelB))
    .join('');
};

/**
 *
 * @param a first node containing row value for custom field
 * @param b second node containing row val for custom field
 * @param customFieldKey ID of the custom field for lookup on row nodes
 * @param option contains custom field info, we need contained labels for multi-select sorting
 */
export const customFieldsComparator = (
  a: RowNode,
  b: RowNode,
  customFieldKey: string,
  option: CustomFieldOption,
) => {
  let valueA = a.data[customFieldKey];
  let valueB = b.data[customFieldKey];
  if (typeof valueA === 'undefined' && typeof valueB !== 'undefined') {
    // in ascending order we put undefined at end of sort
    return 1;
  }
  if (typeof valueA !== 'undefined' && typeof valueB === 'undefined') {
    return -1;
  }
  // if values are objects (only arrays for now) - we know they are multi-select and need to become sortable strings
  if (typeof valueA === 'object') {
    valueA = getMultiSelectSortString(valueA, option.options || []);
  }
  if (typeof valueB === 'object') {
    valueB = getMultiSelectSortString(valueB, option.options || []);
  }

  // alphanumeric values sorted by .localeCompare
  return (valueA || '').localeCompare(valueB || '');
};
